import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { DigitalConcierge } from 'app/components/DigitalConcierge'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { Media } from 'app/theme/media'
import { NewsletterFormSender } from 'app/utils/NewsletterFormSender'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Facebook, Instagram } from 'react-feather'

import { Newsletter } from './Newsletter'

export interface Props {
  address?: string
  appSubtitle?: string
  appTitle?: string
  cin?: string
  facebookURL?: string
  IBEID?: number
  instagramURL?: string
  languageCode: string
  languagePrefix?: string | null
  logo?: string
  newsletterURL?: string
  pdf?: string
  phone?: string
  siteName?: string
  vat?: string
  whatsappURL?: string
  variant?: Variant
}

export const Footer = memo(function Footer({
  address,
  appSubtitle,
  appTitle,
  cin,
  facebookURL,
  instagramURL,
  languageCode,
  languagePrefix,
  logo,
  pdf,
  phone,
  newsletterURL,
  siteName,
  vat,
  whatsappURL,
  variant = 'default',
}: Props) {
  const [newsletterModalStatus, setNewsletterModalStatus] = useState(false)
  const [_locked, setLocked] = useLockedBody()

  const sendFormTo = useMemo(
    () =>
      NewsletterFormSender({
        formURL: 'https://blusuite.emailsp.com/frontend/subscribe.aspx',
      }),
    [],
  )

  return (
    <Container>
      <Wrapper variant={variant}>
        <Head row space="between" wrap>
          {logo ? (
            <Logo src={logo} alt={siteName} width="166" height="98" />
          ) : null}
          <Cell>
            <Label>{useVocabularyData('contacts', languageCode)}</Label>
            {address ? <Address>{address}</Address> : null}
            {vat ? (
              <Vat>{`${useVocabularyData('vat', languageCode)} ${vat}`}</Vat>
            ) : null}
            {cin ? (
              <Cin>{`${useVocabularyData('cin', languageCode)} ${cin}`}</Cin>
            ) : null}
            {phone ? (
              <Phone
                href={`tel:${phone}`}
                onClick={() => {
                  if (isMobile) {
                    typeof window.gtag !== 'undefined' &&
                      window.gtag('event', 'Click', {
                        event_category: 'Website',
                        event_label: 'Mobile Phone',
                      })
                  }
                }}
              >
                {phone}
              </Phone>
            ) : null}
          </Cell>
          <Cell>
            <Label>{useVocabularyData('newsletter', languageCode)}</Label>
            <NewsletterDescription>
              {useVocabularyData('newsletter-description', languageCode)}
            </NewsletterDescription>
            {newsletterURL ? (
              <NewsletterCTA
                label={useVocabularyData(
                  'subscribe-to-newsletter',
                  languageCode,
                )}
                onClick={() => {
                  if (!newsletterModalStatus) {
                    setNewsletterModalStatus(true)
                    if (window !== undefined && window.innerWidth > 1023) {
                      setLocked(true)
                    }
                  } else {
                    setNewsletterModalStatus(false)
                  }
                }}
              />
            ) : null}
          </Cell>
        </Head>
        <Bottom dial={7} row space="between" wrap>
          <Media lessThan="desktopSmall">
            {appTitle || appSubtitle ? (
              <DigitalConcierge title={appTitle} subtitle={appSubtitle} />
            ) : null}
          </Media>
          <Social dial={4} row>
            {facebookURL ? (
              <SocialItem
                aria-label="Facebook"
                href={facebookURL}
                rel="noreferrer"
                target="_blank"
              >
                <Facebook />
              </SocialItem>
            ) : null}
            {instagramURL ? (
              <SocialItem
                aria-label="Instagram"
                href={instagramURL}
                rel="noreferrer"
                target="_blank"
              >
                <Instagram />
              </SocialItem>
            ) : null}
            {whatsappURL ? (
              <SocialItem
                aria-label="Whatsapp"
                href={whatsappURL}
                rel="noreferrer"
                target="_blank"
              >
                <svg
                  className="whatsapp"
                  xmlns="http://www.w3.org/2000/svg"
                  width="25.364"
                  height="24.958"
                  viewBox="0 0 25.364 24.958"
                >
                  <g transform="translate(-8.16 -9.869)">
                    <path
                      d="M40.228,37.524a1.435,1.435,0,0,0-.674-.485l-.189-.092c-.018,0-2.053-1-2.44-1.144a1.011,1.011,0,0,0-1.324.345c-.2.305-.8,1-1.077,1.312-.024.027-.043.04-.043.043a.5.5,0,0,1-.177-.064c-.061-.031-.149-.067-.253-.113A8.662,8.662,0,0,1,31.577,35.8a9.934,9.934,0,0,1-1.864-2.288l-.043-.049a1.112,1.112,0,0,1,.1-.107c.122-.119.253-.278.378-.427l.177-.207a2.521,2.521,0,0,0,.366-.577l.046-.095a1.07,1.07,0,0,0-.027-1.019c-.055-.107-.418-.976-.683-1.611l-.387-.93c-.326-.772-.747-.869-1.138-.872h-.75a1.748,1.748,0,0,0-1.272.58l-.064.067a4.27,4.27,0,0,0-1.278,3.13,7.039,7.039,0,0,0,1.525,3.856l.052.076a15.624,15.624,0,0,0,6.14,5.292,20.572,20.572,0,0,0,2.047.744,5.007,5.007,0,0,0,1.5.226,5.855,5.855,0,0,0,.915-.073,4.061,4.061,0,0,0,2.745-1.94,3.088,3.088,0,0,0,.165-2.041ZM39.2,39.251a3.272,3.272,0,0,1-2.01,1.351,4.508,4.508,0,0,1-2.013-.122,20.269,20.269,0,0,1-1.961-.714,14.663,14.663,0,0,1-5.75-4.981c-.034-.049-.058-.082-.076-.1a6.29,6.29,0,0,1-1.339-3.3,3.373,3.373,0,0,1,1.037-2.5l.073-.076a.845.845,0,0,1,.61-.287h.756c.1,0,.156,0,.287.305.1.229.241.567.39.915.4.958.647,1.55.708,1.672s.049.156.031.192l-.052.1a1.459,1.459,0,0,1-.238.384l-.186.223a4.592,4.592,0,0,1-.305.357.955.955,0,0,0-.226,1.266,10.793,10.793,0,0,0,2.047,2.507A9.606,9.606,0,0,0,33.7,38.134l.21.092a1,1,0,0,0,1.306-.192c.177-.2.891-1.022,1.15-1.4.024-.037.043-.055.046-.061a1.06,1.06,0,0,1,.214.061c.275.1,1.9.882,2.355,1.1l.2.1c.095.043.2.1.268.134A3.327,3.327,0,0,1,39.2,39.251Z"
                      transform="translate(-11.799 -12.337)"
                    />
                    <path
                      d="M30.012,13.725q-.1-.1-.2-.2a12.811,12.811,0,0,0-17.921,0A12.259,12.259,0,0,0,9.969,28.738L8.3,34.116a.46.46,0,0,0,.116.458.452.452,0,0,0,.454.116l5.491-1.644a12.753,12.753,0,0,0,15.45-1.885A12.329,12.329,0,0,0,30.012,13.725Zm-.669,16.6q-.092.094-.186.186v0a11.844,11.844,0,0,1-14.519,1.647.443.443,0,0,0-.238-.067.481.481,0,0,0-.214.052L9.429,33.567,10.874,28.9a.46.46,0,0,0,0-.464,11.353,11.353,0,0,1,1.656-14.26,11.9,11.9,0,0,1,16.627,0A11.417,11.417,0,0,1,29.344,30.324Z"
                      transform="translate(0)"
                    />
                  </g>
                </svg>
              </SocialItem>
            ) : null}
          </Social>
          <Policy row wrap>
            {pdf ? (
              <Announcement href={pdf} rel="noreferrer" target="_blank">
                {useVocabularyData('announcement-text', languageCode)}
              </Announcement>
            ) : null}

            <Cookie
              onClick={() =>
                // @ts-ignore
                Cookiebot.show()
              }
            >
              {useVocabularyData('cookie-policy', languageCode)}
            </Cookie>
            <Link
              to={`${
                languagePrefix ? `/${languagePrefix}` : ''
              }/privacy-policy`}
            >
              {useVocabularyData('privacy-policy', languageCode)}
            </Link>
          </Policy>
          <Credits href="https://www.qnt.it" target="_blank" rel="noopener">
            QNT Hospitality
          </Credits>
        </Bottom>
      </Wrapper>
      <PartnersLogos dial={5} row wrap>
        <Partner
          alt="Tripadvisor Travelers' Choice 2023"
          title="Tripadvisor Travelers' Choice 2023"
          src="/ta.png"
          width="45"
          height="48"
        />
        <Partner
          alt="Tripadvisor"
          title="Tripadvisor"
          src="/tripadvisor-cert.png"
          width="64"
          height="48"
        />
        <Partner alt="Trivago" src="/trivago.png" width="48" height="48" />
        <Partner
          alt="Bino Family"
          title="Bino Family"
          src="/bino-family.png"
          width="98"
          height="48"
        />
        <Partner
          alt="Legambiente Turismo"
          title="Legambiente Turismo"
          src="/legambiente-turismo.png"
          width="109"
          height="48"
        />
        <Partner
          alt="Blu Suite Village 4 All"
          title="Blu Suite Village 4 All"
          src="/blusuite-village-4-all.png"
          width="49"
          height="48"
        />
        <Anchor
          href="https://www.terrabici.com/bike-hotel/blu-suite-resort"
          rel="noopener"
          target="_blank"
        >
          <Partner
            alt="TerraBici"
            title="TerraBici"
            src="/blusuite-partner-terrabici.png"
            width="158"
            height="48"
          />
        </Anchor>
      </PartnersLogos>
      <Modal
        className={newsletterModalStatus ? 'open' : ''}
        onClick={() => {
          setLocked(false)
          setNewsletterModalStatus(false)
        }}
      >
        <ModalWrapper onClick={(e) => e.stopPropagation()}>
          <ModalClose
            onClick={() => {
              setLocked(false)
              setNewsletterModalStatus(false)
            }}
          />
          <Newsletter
            languageCode={languageCode}
            onNewsletterFormSendTo={async (data) => await sendFormTo(data)}
          />
        </ModalWrapper>
      </Modal>
    </Container>
  )
})

const Container = styled.section`
  @media (max-width: 1199px) {
    padding-bottom: 4.3125rem;
  }
`

const Wrapper = styled.div<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  color: ${({ theme }) => theme.colors.variants.neutralDark1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.875rem;
  letter-spacing: 0.0175rem;
  line-height: 1.25rem;
  padding: 11.1875rem 10vw 3.75rem;

  ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          padding-top: 5.875rem;

          @media (max-width: 1199px) {
            padding-top: 2.625rem;
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    padding: 8.75rem 8.454vw 2.625rem 8.454vw;
  }

  @media (max-width: 1023px) {
    padding-top: 2.625rem;
  }
`

const Head = styled(FlexBox)`
  @media (max-width: 1199px) {
    display: block;
    text-align: center;
  }
`

const Logo = styled.img``

const Cell = styled.div`
  max-width: 356px;

  @media (max-width: 1199px) {
    margin: 3.75rem auto 0;
  }
`

const Label = styled.div`
  font-weight: 600;
  letter-spacing: 0.1313rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`

const Address = styled.div``

const Vat = styled.div``

const Cin = styled.div``

const Phone = styled.a`
  display: inline-block;
  margin-top: 2.25rem;
  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 1199px) {
    margin-top: 1.25rem;
  }
`

const NewsletterDescription = styled.div``

const NewsletterCTA = styled(Button)`
  margin-top: 1.5rem;
`

const Bottom = styled(FlexBox)`
  margin-top: 6.875rem;

  @media (max-width: 1199px) {
    display: block;
    margin-top: 3.75rem;
    text-align: center;
  }
`

const Social = styled(FlexBox)`
  @media (max-width: 1199px) {
    justify-content: center;
    margin-bottom: 3.75rem;
  }
`

const SocialItem = styled.a`
  margin-right: 1.25rem;
  &:last-of-type {
    margin-right: 0;
  }
  svg {
    width: auto;
    height: 1.375rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralDark1};
    stroke-width: 1.5px;
    &:hover {
      stroke: ${({ theme }) => theme.colors.variants.neutralDark3};
    }
    &.whatsapp {
      stroke-width: 0.7px;
    }
  }
`

const Policy = styled(FlexBox)`
  a {
    font-size: 0.75rem;
    line-height: 1.25rem;
    margin: 0 0.625rem;
    text-decoration: underline;
    &:hover {
      color: ${({ theme }) => theme.colors.variants.neutralDark2};
    }
  }

  @media (max-width: 1199px) {
    justify-content: center;
    margin-bottom: 1.25rem;
  }
`

const Announcement = styled.a`
  cursor: pointer;

  @media (max-width: 767px) {
    width: 100%;
    margin-bottom: 1.25rem !important;
  }
`

const Cookie = styled.a`
  cursor: pointer;
`

const Credits = styled.a`
  font-size: 0.75rem;
  line-height: 1.25rem;
  margin: 0 0.625rem;
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.variants.neutralDark2};
  }
`

const PartnersLogos = styled(FlexBox)`
  padding: 0 1.5rem 2.25rem;
`

const Partner = styled.img`
  margin: 2.25rem 3.0625rem 0;

  @media (max-width: 1023px) {
    margin: 2.25rem 0.75rem 0;
  }
`

const Anchor = styled.a``

const Modal = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${rgba(theme.colors.variants.neutralDark1, 0.8)};
  cursor: pointer;
  opacity: 0;
  padding: 3.75rem 10vw;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.15s ease-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 99;
  }

  @media (max-width: 1439px) {
    padding-right: 2.1875rem;
    padding-left: 2.1875rem;
  }

  @media (max-width: 1199px) {
    display: block;
    overflow-y: auto;
    padding-top: 7.5rem;
    padding-right: 8.454vw;
    padding-left: 8.454vw;
    -webkit-overflow-scrolling: auto;
  }
`

const ModalWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  cursor: default;
  margin: auto;
  position: relative;

  @media (max-width: 1439px) {
    max-width: none;
  }
`

const ModalClose = styled.div`
  width: 26px;
  height: 26px;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  cursor: pointer;
  position: absolute;
  top: 2.375rem;
  right: 2.375rem;
  z-index: 2;
  &:hover {
    &:before,
    &:after {
      transform: none;
    }
  }
  &:before,
  &:after {
    content: '';
    width: 36px;
    height: 1px;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    position: absolute;
    top: 12px;
    left: -5px;
    transition: 0.3s ease-out;
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }

  @media (max-width: 1023px) {
    top: 1.25rem;
    right: 1.25rem;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'compact'
